/**
 * Admin — Field Agent Activity Detail (READ-ONLY)
 * /admin/field-agents/:agent_id/activities/:activity_id
 * Phase 2C (2026-04-23)
 *
 * Shows one activity + evidence gallery via the gated evidence /file endpoint.
 * NO write actions. Visibility only.
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Loader2, ArrowLeft, AlertTriangle, MapPin, User, Clock, FileText } from "lucide-react";
import ActivityReviewActionsPanel from "../../components/admin/ActivityReviewActionsPanel";
import PayoutReadinessPill from "../../components/fieldAgent/PayoutReadinessPill";


function fmtIso(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-JM"); } catch { return iso; }
}

export default function AdminFieldAgentActivityDetail() {
  const { agent_id, activity_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState(null);

  const reload = async () => { try { const res = await api.get(`/field-activities/${activity_id}`); setActivity(res.data.activity); } catch (e) {} };
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/field-activities/${activity_id}`);
        setActivity(res.data.activity);
      } catch (e) {
        setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not load activity");
      } finally { setLoading(false); }
    })();
  }, [activity_id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  if (error || !activity) {
    return (
      <div className="min-h-screen bg-slate-50 p-6" data-testid="admin-activity-detail-page">
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div className="mt-3 bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700" data-testid="admin-activity-error">{String(error || "Not found")}</div>
        </div>
      </div>
    );
  }

  const evidence = (activity.evidence || []).filter((e) => e.status !== "soft_deleted");
  const dup = activity.duplicate_signal || {};
  const terr = activity.territory_decision;

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="admin-activity-detail-page">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/field-agents/${agent_id}/weekly-rollup`)}><ArrowLeft className="w-4 h-4 mr-1" /> Weekly rollup</Button>
          <Badge variant="outline" data-testid="admin-activity-readonly">Admin actions</Badge>
          <PayoutReadinessPill activity={activity} />
        </div>

        <ActivityReviewActionsPanel activity={activity} onReload={reload} />
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-semibold">Activity {activity_id}</h1>
            <Badge className={activity.status === "submitted" ? "bg-emerald-600" : "bg-amber-500"}>{activity.status}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs text-slate-500">Date</div><div className="font-medium">{activity.activity_date}</div></div>
            <div><div className="text-xs text-slate-500">Visit channel</div><div className="font-medium">{activity.visit_channel}</div></div>
            <div><div className="text-xs text-slate-500">Area / Parish</div><div className="font-medium"><MapPin className="w-3 h-3 inline mr-1" />{activity.area} · {activity.parish}</div></div>
            <div><div className="text-xs text-slate-500">Time</div><div className="font-medium"><Clock className="w-3 h-3 inline mr-1" />{activity.time_started || "—"} – {activity.time_ended || "—"}</div></div>
            <div><div className="text-xs text-slate-500">Outcome</div><div className="font-medium">{activity.visit_outcome}</div></div>
            <div><div className="text-xs text-slate-500">Intent</div><div className="font-medium">{activity.conversion_intent}</div></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3" data-testid="admin-activity-prospect">
          <h2 className="text-sm font-semibold mb-2 flex items-center gap-1"><User className="w-4 h-4" /> Prospect</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs text-slate-500">Type</div><div className="font-medium">{activity.prospect?.type}</div></div>
            <div><div className="text-xs text-slate-500">Name</div><div className="font-medium">{activity.prospect?.name || "—"}</div></div>
            <div><div className="text-xs text-slate-500">Phone</div><div className="font-mono text-xs">{activity.prospect?.phone_normalized || activity.prospect?.phone || "—"}</div></div>
            <div><div className="text-xs text-slate-500">Business</div><div className="font-medium">{activity.prospect?.business_type || "—"}</div></div>
            <div className="col-span-2"><div className="text-xs text-slate-500">Address</div><div className="text-sm">{activity.prospect?.address || "—"}</div></div>
          </div>
        </div>

        {dup?.score > 0 && (
          <div className={`rounded-xl border p-4 mb-3 ${dup.score >= 0.9 ? "bg-rose-50 border-rose-200" : "bg-amber-50 border-amber-200"}`} data-testid="admin-activity-duplicate">
            <div className="flex items-start gap-2">
              <AlertTriangle className={`w-4 h-4 mt-0.5 ${dup.score >= 0.9 ? "text-rose-600" : "text-amber-600"}`} />
              <div className="text-sm flex-1">
                <div className="font-medium">Duplicate signal · score {dup.score.toFixed(2)}</div>
                <div className="text-xs text-slate-600">Primary: {dup.primary_reason || "—"} · Contributing: {(dup.contributing_reasons || []).join(", ") || "—"}</div>
                {dup.acknowledged && (
                  <div className="mt-2 text-xs bg-white/70 p-2 rounded border">
                    <div className="font-medium">Agent acknowledged</div>
                    <div className="text-slate-600">At {fmtIso(dup.acknowledged.at)}</div>
                    <div className="italic mt-1">"{dup.acknowledged.reason}"</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {(activity.territory_warning || terr === "blocked_exclusive") && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 mb-3 text-sm text-indigo-800" data-testid="admin-activity-territory">
            <MapPin className="w-3 h-3 inline mr-1" /> Territory: {terr || "out_of_territory_soft"}
          </div>
        )}

        {(activity.notes || activity.next_action || activity.follow_up_date) && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
            <h2 className="text-sm font-semibold mb-2 flex items-center gap-1"><FileText className="w-4 h-4" /> Notes & follow-up</h2>
            {activity.notes && <div className="text-sm mb-2">{activity.notes}</div>}
            {activity.next_action && <div className="text-xs text-slate-600">Next: {activity.next_action}</div>}
            {activity.follow_up_date && <div className="text-xs text-slate-600">Follow-up due: {activity.follow_up_date}</div>}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="admin-activity-evidence">
          <h2 className="text-sm font-semibold mb-2">Evidence ({evidence.length})</h2>
          {evidence.length === 0 ? (
            <p className="text-xs text-slate-400">No evidence attached.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {evidence.map((e) => (
                <a key={e.id} href={`/api/field-activities/${activity_id}/evidence/${e.id}/file`} target="_blank" rel="noreferrer" className="block border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition" data-testid={`admin-evidence-${e.id}`}>
                  <div className="bg-slate-100 h-32 flex items-center justify-center">
                    <img src={`/api/field-activities/${activity_id}/evidence/${e.id}/file`} alt={e.type} className="max-h-32 object-contain" />
                  </div>
                  <div className="p-2 text-xs">
                    <div className="font-medium">{e.type?.replace(/_/g, " ")}</div>
                    <div className="text-slate-500">{Math.round(e.size_bytes / 1024)} KB · {fmtIso(e.uploaded_at)}</div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
